import React, { useContext, useEffect, useState } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import Icon from "../ui/Icon";
import Badge from "../ui/Badge";
import { UserContext, useEntitlements } from "../../contexts";
import { canUseOpenedFeature } from "../../contexts/openedFeature";
import { useOnMasterStore } from "../../helpers/storeScope";
import { StoreMark } from "./AppSidebar";
import { canUseWaBroadcast } from "../../waBroadcast/waBroadcastHelpers";
import { NAV_PRIMARY, PHONE_MORE, phoneTabIds, navById, activeNavId, isNavHidden } from "./navigation";
import "./BottomTabBar.scss";

// Fontaine M29 — phone navigation: bottom tab bar (Listings · Orders · Requests · Trade Feed · More) and the
// More bottom sheet with the remaining sections (Board 2 Q3 = A). Replaces react-burger-menu + react-headroom.

function MoreSheet({ onClose }) {
  const { pathname } = useLocation();
  const history = useHistory();
  const entitlements = useEntitlements();
  const { hasFeature } = entitlements;
  const onMaster = useOnMasterStore();
  const { user, activeStoreId, accessibleStores, setActiveStore } = useContext(UserContext) ?? {};
  const isMasterAdmin = !!user?.is_master_admin;
  const canOpen = (key) => canUseOpenedFeature(user, entitlements, key);
  const stores = (accessibleStores || []).filter((s) => s && s.id != null);
  const current = stores.find((s) => s.id === activeStoreId) ?? null;
  const activeId = activeNavId(pathname);
  // masterOnly rows are hidden, not locked — same rule as AppSidebar, keyed off
  // the role rather than the active store, because that is what the API gate
  // behind them checks.
  const tabIds = phoneTabIds(isMasterAdmin, canOpen);
  const items = PHONE_MORE.map(navById).filter(Boolean).filter((i) => !tabIds.includes(i.id)).filter((i) => !(i.hideOnMaster && onMaster)).filter((i) => !isNavHidden(i, isMasterAdmin, canOpen, hasFeature)).filter((i) => i.id !== "uptime" || hasFeature("uptime_monitor")).filter((i) => !i.waBroadcast || canUseWaBroadcast(user, accessibleStores, activeStoreId, hasFeature));

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);

  return createPortal(
    <div className="ft-more__scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ft-more" role="dialog" aria-modal="true" aria-label="More">
        <div className="ft-more__head">
          <span className="ft-more__title">More</span>
          <button type="button" className="ft-more__close" aria-label="Close" onClick={onClose}><Icon name="X" size="sm" /></button>
        </div>
        <div className="ft-more__store">
          <StoreMark store={current} master={onMaster} size="md" />
          <span className="ft-more__store-text">
            <span className="ft-more__store-name">{onMaster ? "Master store" : current?.name || "My store"}</span>
            <span className="ft-more__store-sub">{onMaster ? `${stores.length} stores` : user?.email}</span>
          </span>
          {stores.length > 1 ? (
            <select className="ft-more__switch" aria-label="Switch store" value={activeStoreId ?? ""} onChange={(e) => { const id = e.target.value; onClose(); if (id) setActiveStore?.(isNaN(+id) ? id : +id); }}>
              {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          ) : null}
        </div>
        <ul className="ft-more__list">
          {items.map((i) => {
            const locked = !!i.feature && !hasFeature(i.feature);
            // Locked for everyone (core/config/dashboard.js): stays in the list
            // with its lock, but goes nowhere.
            const pageLocked = !!i.pageLocked;
            return (
              <li key={i.id}>
                <button type="button" className={`ft-more__item${activeId === i.id ? " is-active" : ""}${locked ? " is-locked" : ""}`} aria-disabled={locked || pageLocked || undefined} disabled={pageLocked || undefined} onClick={() => { if (pageLocked) return; onClose(); history.push(i.to); }}>
                  <span className="ft-more__icon" aria-hidden="true"><Icon name={i.icon} size="md" /></span>
                  <span className="ft-more__label">{i.label}</span>
                  {pageLocked ? <Icon name="Lock" size="sm" /> : locked ? <Badge tone="muted" size="sm">Plan</Badge> : <Icon name="ChevronRight" size="sm" />}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>,
    document.body
  );
}

export default function BottomTabBar() {
  const { pathname } = useLocation();
  const entitlements = useEntitlements();
  const { hasFeature } = entitlements;
  const { user } = useContext(UserContext) ?? {};
  const [more, setMore] = useState(false);
  const activeId = activeNavId(pathname);
  // Via phoneTabIds, not PHONE_TABS directly: Trade Feed holds the fourth slot
  // but is master-only, and this bar has never filtered on that the way the
  // More sheet does. Without the guard every non-master user would get a tab to
  // a page the API refuses.
  const tabs = phoneTabIds(!!user?.is_master_admin, (key) => canUseOpenedFeature(user, entitlements, key)).map(navById).filter(Boolean).filter((t) => !(t.hideLocked && !hasFeature(t.feature)));
  const moreActive = !tabs.some((t) => t.id === activeId) && PHONE_MORE.includes(activeId);
  useEffect(() => { setMore(false); }, [pathname]);

  return (
    <>
      <nav className="ft-tabbar" aria-label="Primary">
        {tabs.map((t) => {
          const locked = !!t.feature && !hasFeature(t.feature);
          return (
            <NavLink key={t.id} to={t.to} className={`ft-tabbar__tab${activeId === t.id ? " is-active" : ""}${locked ? " is-locked" : ""}`} aria-current={activeId === t.id ? "page" : undefined} aria-disabled={locked || undefined}>
              <span className="ft-tabbar__icon" aria-hidden="true"><Icon name={t.icon} size="md" />{locked ? <span className="ft-tabbar__lock"><Icon name="Lock" size="xs" /></span> : null}</span>
              <span className="ft-tabbar__label">{t.label === "Buy Alerts" ? "Alerts" : t.label}</span>
            </NavLink>
          );
        })}
        <button type="button" className={`ft-tabbar__tab${moreActive || more ? " is-active" : ""}`} aria-haspopup="dialog" aria-expanded={more} onClick={() => setMore(true)}>
          <span className="ft-tabbar__icon" aria-hidden="true"><Icon name="MoreHorizontal" size="md" /></span>
          <span className="ft-tabbar__label">More</span>
        </button>
      </nav>
      {more ? <MoreSheet onClose={() => setMore(false)} /> : null}
    </>
  );
}

export { NAV_PRIMARY };
